<template>
    <div class="versus-options">
        <div class="versus-options__cards">
            <MaltImg v-if="logoUrl" class="versus-options__logo" :src="logoUrl" :alt="logoAlt ?? title ?? ''" :height="50" />
            <div v-for="(option, optionIndex) in options" :key="`option-card-${optionIndex}`" class="versus-options__card">
                <div class="versus-options__card-header">
                    <div v-if="option.type === 'title'" class="versus-options__card-header--text">
                        {{ option.text }}
                    </div>
                    <MaltImg v-if="option.type === 'logo'" :src="option.url" :alt="option.alt ?? ''" :height="25" />
                </div>
                <ul class="versus-options__card-features">
                    <li
                        v-for="(feature, featureIndex) in features"
                        :key="`option-card-feature-${optionIndex}-${featureIndex}`"
                        class="versus-options__card-feature"
                    >
                        <span>{{ feature }}</span>
                        <VJoyIcon v-if="optionsChecks[optionIndex]?.[featureIndex]?.check" name="check" color="secondary" />
                        <VJoyIcon v-else name="cross" color="primary" />
                    </li>
                </ul>
            </div>
        </div>
        <div class="versus-options__grid">
            <MaltImg v-if="logoUrl" class="versus-options__logo" :src="logoUrl" :alt="logoAlt ?? title ?? ''" :height="50" />
            <div class="versus-options__cell versus-options__cell--featureHeader">
                {{ featuresTitle }}
            </div>
            <div
                v-for="(option, optionIndex) in options"
                :key="`option-header-${optionIndex}`"
                class="versus-options__cell versus-options__cell--header"
                :class="{'versus-options__cell--firstOption': optionIndex === 0}"
            >
                <div v-if="option.type === 'title'" class="versus-options__cell--header-text">
                    {{ option.text }}
                </div>
                <MaltImg v-if="option.type === 'logo'" :src="option.url" :alt="option.alt ?? ''" :height="25" />
            </div>

            <template v-for="(feature, featureIndex) in features" :key="`option-feature-${featureIndex}`">
                <div class="versus-options__cell versus-options__cell--feature">
                    {{ feature }}
                </div>
                <div
                    v-for="(_, optionIndex) in options"
                    :key="`option-value-${featureIndex}-${optionIndex}`"
                    class="versus-options__cell versus-options__cell--value"
                    :class="{
                        'versus-options__cell--firstOption': optionIndex === 0,
                        'versus-options__cell--lastRow': featureIndex === features.length - 1,
                    }"
                >
                    <div class="versus-options__value">
                        <VJoyIcon v-if="optionsChecks[optionIndex]?.[featureIndex]?.check" name="check" color="secondary" />
                        <VJoyIcon v-else name="cross" color="primary" />
                        <span v-if="optionsChecks[optionIndex]?.[featureIndex]?.note" class="versus-options__note">
                            {{ optionsChecks[optionIndex]?.[featureIndex]?.note }}
                        </span>
                    </div>
                </div>
            </template>
        </div>
        <VJoyButton v-if="ctaUrl" class="versus-options__cta" :variant="buttonVariant" :href="$withLocalePrefix(ctaUrl)" rel="nofollow">
            {{ ctaText }}
        </VJoyButton>
    </div>
</template>
<script setup lang="ts">
    import {MaltImg} from '#components';
    import {VJoyButton, VJoyIcon} from '@maltjoy/core-vue';
    import {useVariants} from '../../composables/useVariants';

    export type OptionHeader = {type: 'title'; text: string} | {type: 'logo'; url: string; alt: string};
    export type OptionValue = {check: boolean; note?: string};

    defineProps<{
        title: string | null;
        logoUrl?: string | null;
        logoAlt?: string | null;
        featuresTitle: string;
        features: string[];
        options: OptionHeader[];
        optionsChecks: OptionValue[][];
        ctaText: string | null;
        ctaUrl: string | null;
    }>();

    const {button: buttonVariant} = useVariants();
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $border-color: var(--joy-color-neutral-30);
    $border-radius: var(--joy-core-radius-4);
    $grid-feature-max-width: 400px;
    $grid-option-max-width: 180px;

    .versus-options {
        text-align: center;

        &__title {
            text-align: left;
            font-weight: var(--joy-font-weight-bold);
            line-height: var(--joy-line-height-large);
            letter-spacing: var(--joy-font-letter-spacing-tight);
            margin-bottom: var(--joy-core-spacing-8);
        }

        &__logo {
            margin-bottom: var(--joy-core-spacing-8);
        }

        &__grid {
            display: none;
        }

        &__cards {
            display: grid;
            gap: var(--joy-core-spacing-4);
        }

        &__card {
            border: 1px solid $border-color;
            border-radius: $border-radius;
            padding: var(--joy-core-spacing-8);

            &:first-of-type {
                background-color: var(--joy-color-neutral-10);
            }
        }

        &__card-header {
            text-align: left;
            margin-bottom: var(--joy-core-spacing-6);

            &--text {
                font-size: var(--joy-font-size-primary-600);
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-large);
                letter-spacing: var(--joy-font-letter-spacing-tight);
            }
        }

        &__card-features {
            list-style: none;
            padding: 0;
            margin: 0;
        }

        &__card-feature {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--joy-core-spacing-2) 0;
            text-align: left;
        }

        &__cta {
            margin: var(--joy-core-spacing-8) auto;
        }

        @include mq.screen_md(min) {
            &__cards {
                display: none;
            }

            &__title,
            &__logo {
                grid-column-end: span calc(1 + v-bind('options.length'));
            }

            &__grid {
                display: grid;
                justify-content: center;
                grid-template-columns: 1.5fr repeat(v-bind('options.length'), 1fr);
                column-gap: var(--joy-core-spacing-4);
            }

            &__cell {
                width: 100%;
                text-align: center;
                padding: var(--joy-core-spacing-3);

                &--featureHeader {
                    text-align: left;
                    padding-left: 0;
                    padding-top: var(--joy-core-spacing-8);
                    padding-bottom: var(--joy-core-spacing-6);

                    font-size: var(--joy-font-size-primary-600);
                    font-weight: var(--joy-font-weight-bold);
                    line-height: var(--joy-line-height-large);
                    letter-spacing: var(--joy-font-letter-spacing-tight);
                }

                &--header {
                    border: 1px solid $border-color;
                    border-bottom: none;
                    border-radius: $border-radius $border-radius 0 0;
                    padding-top: var(--joy-core-spacing-8);
                    padding-bottom: var(--joy-core-spacing-6);

                    &-text {
                        font-size: var(--joy-font-size-primary-400);
                        font-weight: var(--joy-font-weight-bold);
                        line-height: var(--joy-line-height-large);
                        letter-spacing: var(--joy-font-letter-spacing-tight);
                    }
                }

                &--feature {
                    text-align: left;
                    padding-left: 0;
                }

                &--value {
                    border-left: 1px solid $border-color;
                    border-right: 1px solid $border-color;
                }

                &--lastRow {
                    border-bottom: 1px solid $border-color;
                    border-radius: 0 0 $border-radius $border-radius;
                    padding-bottom: var(--joy-core-spacing-6);
                }

                &--firstOption {
                    background-color: var(--joy-color-neutral-10);
                }
            }

            &__value {
                display: flex;
                align-items: flex-start;
                justify-content: center;
                gap: var(--joy-core-spacing-2);
            }

            &__note {
                font-size: var(--joy-font-size-primary-300);
                font-weight: var(--joy-font-weight-normal);
                line-height: var(--joy-line-height-large);
                letter-spacing: var(--joy-font-letter-spacing-tight);
            }
        }
    }
</style>
